import {
  ApolloError,
  NetworkStatus,
  OperationVariables,
  useApolloClient,
} from '@apollo/client';
import * as React from 'react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { ImageWithLoader } from '../../components/ImageWithLoader';
import PostCategories from '../../components/Post/PostCategories';
import TextEditor from '../../components/TextEditor';
import ButtonComponent from '../../components/UI/Button/Button.component';
import {
  POST,
  POST_BUTTON_LABELS,
  REPLY_DRAWER_PREVIEW_CHARS,
} from '../../constants/post.constants';
import { POST_REPLY_DRAFT } from '../../constants/postReplyDraft.constants';
import { useAuth } from '../../context/AuthContext';
import { useSocket } from '../../context/WebsocketContext';
import { formatDate } from '../../helpers/dateConverters';
import { renderLinkedText } from '../../helpers/renderLinkedText';
import { truncateReplyPreview } from '../../helpers/truncateReplyPreview';
import { useReplies } from '../../hooks/useReplies';
import { useStoryScoreUpdates } from '../../hooks/useStoryScoreUpdates';
import { FeedAuthor, FeedPost } from '../../interfaces/feed.interface';
import {
  PostReplyDraftInterfaceState,
  PostReplyInterface,
} from '../../interfaces/post.interface';
import type { TextBlock } from '../../interfaces/textEditor.interface';
import type { ReplyNotificationData } from '../../interfaces/websocket.interface';
import { draftSlice } from '../../store/draft.store';
import '../../styles/post.scss';
import '../../styles/utilities.scss';
import '../Dialogs/TextAnalysisResponse/text-analysis-drawer.scss';

const REPLIES_DRAWER_WIDTH = 420;

interface PostReplies {
  items: PostReplyInterface[];
  nextToken: string | null;
  loading: boolean;
  error: ApolloError | undefined;
  loadMore: () => Promise<void>;
  refetch: (
    variables?: Partial<OperationVariables> | undefined,
  ) => Promise<any>;
  networkStatus: NetworkStatus;
}

export interface UserPostComponentHandle {
  openDrawer: () => void;
  openReplyModal: () => void;
}

interface UserPostComponentProps {
  post: FeedPost;
  postReplyDraftId: string | undefined;
  username: string;
  status: string | undefined;
  onScoreUpdate?: (newScore: number) => void;
  onReplyCountUpdate?: (newCount: number) => void;
}

const UserPostComponent = forwardRef<
  UserPostComponentHandle,
  UserPostComponentProps
>((props, ref) => {
  const {
    post,
    postReplyDraftId,
    username,
    status,
    onScoreUpdate,
    onReplyCountUpdate,
  } = props;
  const { user } = useAuth();
  const client = useApolloClient();
  const { sendMessage, authenticated, onReplyNotification } = useSocket();
  const parentPostId = post.id as string;
  const [currentScore, setCurrentScore] = useState<number | null>(() => {
    if (typeof post?.score === 'number' && post.score !== 0) {
      return post.score;
    }
    const scoreFromAnalysis =
      post?.storyAnalysis?.analysis?.[0]?.storyAnalyzer?.overall_score;
    return typeof scoreFromAnalysis === 'number' ? scoreFromAnalysis : null;
  });
  const [scoreAnimation, setScoreAnimation] = useState<'up' | 'down' | null>(
    null,
  );
  const [drawerLoading, setDrawerLoading] = useState(false);

  const [newRepliesBanner, setNewRepliesBanner] = useState<{
    count: number;
    latestAuthor: string;
  } | null>(null);

  const [isRepliesDrawerOpen, setIsRepliesDrawerOpen] = useState(false);
  const [isReplyModalOpen, setIsReplyModalOpen] = useState(false);

  const dispatch = useDispatch();
  const { isEditingPostReplyDraft } = useSelector(
    (state: PostReplyDraftInterfaceState) => state.postReplyDraftStore,
  );

  // Reply count derives from the cache-backed `post` prop (TRIL-173 / SP2a:
  // the Post detail page reads getPostById via useQuery; every reply-count
  // writer in this slice's scope writes Post:{id}.replyCount via cache.modify).
  // Three converging paths keep it fresh: the WS `new_replies` handler
  // (websocketMiddleware.ts), the optimistic-bump hook (TRIL-191), and the
  // drawer-refetch reconciliation below.
  const replyCount = post.replyCount ?? 0;
  const { IS_ADDING_POST_REPLY_AS_DRAFT_STORE_ACTION, FEED_POST_ID } =
    draftSlice.actions;

  const {
    items: repliesRaw,
    loading: repliesLoading,
    error,
    loadMore: loadMoreReplies,
    nextToken: repliesNextToken,
    refetch: refetchReplies,
  } = useReplies(parentPostId);
  // TRIL-172 SP1: transitional cast — the read path is now codegen-typed
  // (ReplyCardFields), but the reply rendering + Redux still expect
  // PostReplyInterface. Removed in SP2/SP3 when Redux stops owning post reads.
  const replies = repliesRaw as unknown as PostReplyInterface[];

  const { latestUpdate } = useStoryScoreUpdates({
    storyId: parentPostId,
    onUpdate: (data) => {
      setCurrentScore(data.newScore);
      onScoreUpdate?.(data.newScore);
      setScoreAnimation(
        data.scoreDelta.change_direction === 'up'
          ? 'up'
          : data.scoreDelta.change_direction === 'down'
            ? 'down'
            : null,
      );
      setTimeout(() => setScoreAnimation(null), 2000);
      if (data.replyInfluence?.total_replies !== undefined && parentPostId) {
        const totalReplies = data.replyInfluence.total_replies;
        // Apollo cache update — the Post detail page (useQuery) re-renders
        // when Post:{id}.replyCount changes (TRIL-173 + TRIL-191).
        client.cache.modify({
          id: client.cache.identify({ __typename: 'Post', id: parentPostId }),
          fields: { replyCount: () => totalReplies },
        });
        onReplyCountUpdate?.(totalReplies);
      }
    },
  });

  React.useEffect(() => {
    if (replies.length) {
      // Reconcile Redux reply count if the drawer refetch turned up more
      // replies than the store knows about. This covers WS gaps (e.g. a
      // `new_replies` broadcast missed during a reconnect) — next drawer
      // open closes the gap without waiting on another broadcast.
      if (parentPostId && replies.length > replyCount) {
        // Apollo cache update — closes drawer/WS gaps without waiting on
        // another broadcast (TRIL-173 + TRIL-191).
        client.cache.modify({
          id: client.cache.identify({ __typename: 'Post', id: parentPostId }),
          fields: { replyCount: () => replies.length },
        });
        onReplyCountUpdate?.(replies.length);
      }
    }
  }, [replies, dispatch, parentPostId, replyCount]);

  // Subscribe to story updates when the post page mounts
  React.useEffect(() => {
    if (authenticated && parentPostId) {
      sendMessage({ action: 'subscribeToReplies', postId: parentPostId });
    }
    return () => {
      if (authenticated) {
        sendMessage({ action: 'unsubscribeFromReplies' });
      }
    };
  }, [authenticated, parentPostId, sendMessage]);

  useImperativeHandle(ref, () => ({
    openDrawer: () => handleOpenRepliesDrawer(),
    openReplyModal: () => handleAddPostReplyDraft(parentPostId),
  }));

  const handleOpenRepliesDrawer = async () => {
    setIsRepliesDrawerOpen(true);
    setNewRepliesBanner(null);
    setDrawerLoading(true);
    try {
      await refetchReplies();
    } finally {
      setDrawerLoading(false);
    }
    // No need to re-subscribe — already subscribed on mount
  };

  const handleCloseRepliesDrawer = () => {
    setIsRepliesDrawerOpen(false);
    setNewRepliesBanner(null);
    // Do NOT unsubscribe here — the mount/unmount effect handles GSI2PK.
    // Unsubscribing on drawer close removes the user from PostWatchersIndex,
    // causing score updates (arriving 30-60s later) to miss them.
  };

  React.useEffect(() => {
    const unsubscribe = onReplyNotification((data: ReplyNotificationData) => {
      if (data.postId !== parentPostId) return;
      // Redux is already updated by App.tsx's global websocketMiddleware
      // subscription for `new_replies`. We keep this local listener only
      // to drive the in-drawer "new replies" banner and the legacy
      // onReplyCountUpdate callback.
      const newCount =
        (data as any).totalReplyCount ?? replyCount + data.newReplyCount;
      onReplyCountUpdate?.(newCount);
      if (isRepliesDrawerOpen) {
        setNewRepliesBanner((prev) => ({
          count: (prev?.count ?? 0) + data.newReplyCount,
          latestAuthor: data.latestReplyAuthor ?? prev?.latestAuthor ?? '',
        }));
      }
    });
    return unsubscribe;
  }, [
    onReplyNotification,
    parentPostId,
    isRepliesDrawerOpen,
    replyCount,
    onReplyCountUpdate,
  ]);

  const handleBannerClick = async () => {
    setNewRepliesBanner(null);
    await refetchReplies();
  };

  const handleAddPostReplyDraft = (id: string) => {
    setIsReplyModalOpen(true);
    dispatch(IS_ADDING_POST_REPLY_AS_DRAFT_STORE_ACTION(true));
    dispatch(FEED_POST_ID(id));
  };

  const renderBlock = (block: TextBlock) => {
    switch (block.type) {
      case 'Heading': {
        const level = block.data?.level ?? 1;
        const HeadingTag = `h${level}` as React.ElementType;
        return (
          <HeadingTag className={'POST__HEADER'}>
            {block.data?.header}
          </HeadingTag>
        );
      }
      case 'Paragraph':
        return <p>{renderLinkedText(block.data?.paragraph)}</p>;
      case 'List':
        return block.data?.style === 'ordered' ? (
          <ol style={{ paddingLeft: '2rem', listStyleType: 'decimal' }}>
            {block.data?.items?.map((item: any, itemIndex: number) => (
              <li key={itemIndex - item.id}>{item.text}</li>
            ))}
          </ol>
        ) : (
          <ul style={{ paddingLeft: '2rem', listStyleType: 'disc' }}>
            {block.data?.items?.map((item: any, itemIndex: number) => (
              <li key={itemIndex - item.id}>{item.text}</li>
            ))}
          </ul>
        );
      case 'Image':
        return (
          <ImageWithLoader
            src={block.data?.file?.url as string}
            alt="Post Image"
            style={{ maxWidth: '100%', height: 'auto' }}
          />
        );
      case 'Code':
        return (
          <pre
            style={{
              backgroundColor: '#f5f5f5',
              padding: '1rem',
              borderRadius: '4px',
              overflowX: 'auto',
            }}
          >
            <code>{block.data?.code}</code>
          </pre>
        );
      case 'Quote':
        return (
          <blockquote
            style={{
              borderLeft: '4px solid var(--accent-emeraude)',
              paddingLeft: '1rem',
              margin: '1rem 0',
              fontStyle: 'italic',
              color: 'var(--text-secondary)',
            }}
          >
            <p style={{ margin: 0 }}>
              {renderLinkedText(block.data?.paragraph)}
            </p>
            {block.data?.caption && (
              <p
                style={{
                  margin: '0.5rem 0 0',
                  fontSize: '0.875rem',
                  color: 'var(--text-muted)',
                }}
              >
                — {block.data.caption}
              </p>
            )}
          </blockquote>
        );
      default:
        return null;
    }
  };

  const renderReplies = (replies: PostReplyInterface[]) => {
    if (!replies || replies.length === 0) {
      return null;
    }

    const sortedReplies = [...replies].sort(
      (a, b) =>
        new Date(b.createdAt as unknown as string).getTime() -
        new Date(a.createdAt as unknown as string).getTime(),
    );

    return sortedReplies.map((reply) => {
      // TRIL-353: cap the inline preview — long replies snip to a word-boundary
      // snippet + "…"; the full body lives on the reply detail page.
      const preview = truncateReplyPreview(
        (reply.textBlocks ?? []) as TextBlock[],
        REPLY_DRAWER_PREVIEW_CHARS,
      );

      return (
        <div
          key={reply.id}
          className="REPLY_WRAPPER"
          style={{
            borderLeft: '2px solid #e0e0e0',
            paddingLeft: '1rem',
            paddingTop: '0.5rem',
            paddingBottom: '0.5rem',
          }}
        >
          <p>
            @{reply.username} · {formatDate(reply.createdAt as Date)}
          </p>

          {reply.isFollowingAuthor !== null && (
            <p>{reply.isFollowingAuthor ? 'Following' : 'Not following'}</p>
          )}

          <div>
            {preview.truncated ? (
              <p className="REPLY_WRAPPER__preview">{preview.text}</p>
            ) : (
              reply.textBlocks?.map((block, index) => (
                <React.Fragment key={`${block.id}-${index}`}>
                  {renderBlock(block)}
                </React.Fragment>
              ))
            )}
          </div>

          {/* TRIL-349: link to the reply's detail page; TRIL-353: doubles as the
              "read full reply" affordance when the preview is truncated. */}
          <Link
            to={`/reply/${reply.id}`}
            className="REPLY_WRAPPER__detail-link"
          >
            {preview.truncated ? 'Read full reply →' : 'View reply →'}
          </Link>
        </div>
      );
    });
  };

  const authorId =
    (post.author as FeedAuthor)?.authorId ??
    (post.author as FeedAuthor)?.userId ??
    user?.id ??
    null;

  const isOwnPost =
    (!!authorId && authorId === user?.id) ||
    post.author?.username === user?.username;

  return (
    <div className="Container">
      <div>
        {/* ── Draft reply link ── */}
        {status !== undefined && (
          <div className="flex gap-4 PARENT_POST_LINK">
            <Link
              className="read-more-button"
              style={{ textDecoration: 'none' }}
              to={`/post-reply-draft/${postReplyDraftId}`}
            >
              {POST_REPLY_DRAFT.LINK.CONTINUE_EDITING}
            </Link>
          </div>
        )}

        <p>
          {post.createdAt && <>Published on: {formatDate(post.createdAt)}</>}
        </p>
        <PostCategories post={post} />
        {post.createdAt &&
          post.updatedAt &&
          post.createdAt !== post.updatedAt && (
            <p>Updated on: {formatDate(post.updatedAt)}</p>
          )}

        {/* ── Post body ── */}
        {(post.textBlocks ?? []).map((block, index) => (
          <React.Fragment key={`${block.id}-${index}`}>
            {renderBlock(block)}
          </React.Fragment>
        ))}

        {/* ── Replies trigger button — sits below the article ── */}
        <div
          style={{
            marginTop: '2rem',
            paddingTop: '1rem',
            borderTop: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
          }}
        >
          <button
            type="button"
            className="btn btn--outline"
            onClick={handleOpenRepliesDrawer}
          >
            {replyCount === 0
              ? `No ${POST_BUTTON_LABELS.REPLIES}`
              : `${replyCount} ${replyCount === 1 ? POST_BUTTON_LABELS.REPLY : POST_BUTTON_LABELS.REPLIES}`}
          </button>
        </div>
      </div>

      {/* ── Replies Drawer (right) ── */}
      {isRepliesDrawerOpen && (
        <div className="drawer-overlay" onClick={handleCloseRepliesDrawer} />
      )}
      <div
        className={
          isRepliesDrawerOpen
            ? 'drawer drawer--right drawer--open'
            : 'drawer drawer--right'
        }
      >
        {/* Header */}
        <div
          style={{
            padding: '0 1.5rem',
            height: '50px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
            borderBottom: '1px solid #e0e0e0',
          }}
        >
          <p style={{ margin: 0, fontSize: '0.875rem' }}>
            {replyCount === 0
              ? `No replies yet.${isOwnPost ? '' : ' Be the first to respond.'}`
              : `${replyCount} ${replyCount === 1 ? POST_BUTTON_LABELS.REPLY : POST_BUTTON_LABELS.REPLIES}`}
          </p>
          <button
            type="button"
            aria-label="close replies"
            onClick={handleCloseRepliesDrawer}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: '1.25rem',
            }}
          >
            ✕
          </button>
        </div>

        {/* Reply action — pinned below header */}
        {!isOwnPost && (
          <div className="drawer__reply-action">
            <ButtonComponent
              className="drawer__reply-btn"
              variant="primary"
              size="md"
              onClick={() => handleAddPostReplyDraft(post.id as string)}
            >
              {POST.REPLY}
            </ButtonComponent>
          </div>
        )}

        {/* New replies banner */}
        {newRepliesBanner && isRepliesDrawerOpen && (
          <div
            onClick={handleBannerClick}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                handleBannerClick();
              }
            }}
            style={{
              position: 'sticky',
              top: 0,
              zIndex: 10,
              margin: '0.5rem 1rem',
              padding: '0.375rem 1rem',
              borderRadius: '20px',
              backgroundColor: '#1976d2',
              color: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.5rem',
            }}
          >
            <p>
              ↑ {newRepliesBanner.count} new{' '}
              {newRepliesBanner.count === 1 ? 'reply' : 'replies'}
              {newRepliesBanner.latestAuthor
                ? ` · from @${newRepliesBanner.latestAuthor}`
                : ''}
            </p>
          </div>
        )}

        {/* Scrollable replies list */}
        <div className="drawer__scrollable-body">
          {drawerLoading || repliesLoading ? (
            <div>
              <div className="LoadingSpinner" />
            </div>
          ) : (
            <div className="Stack">{renderReplies(replies)}</div>
          )}

          {/* Load more */}
          {repliesNextToken && (
            <div>
              <button
                type="button"
                className="btn btn--text"
                onClick={loadMoreReplies}
              >
                Load more replies
              </button>
            </div>
          )}
        </div>

        {/* Footer spacer */}
      </div>

      {/* TextEditor stays as-is — triggered from inside the drawer */}
      <TextEditor
        isModalOpen={isReplyModalOpen}
        refetch={refetchReplies}
        setIsModalOpen={setIsReplyModalOpen}
        userId={user?.id as string}
        username={user?.username as string}
        isEditing={isEditingPostReplyDraft as boolean}
      />
    </div>
  );
});

export default UserPostComponent;
